import { writable, get } from "svelte/store";
import { browser } from "$app/environment";
import type { SearchResult } from "$lib/types";
import type { List } from "$lib/types";
import { authStore } from "./authStore";
import { listHistoryStore } from "./listHistoryStore";
import { goto } from "$app/navigation";

const LIST_PREFIX = "list_";

function createListStore() {
  const { subscribe, update } = writable<{ [slug: string]: List }>({});

  function saveListToLocalStorage(state: List) {
    if (!browser) return;

    try {
      localStorage.setItem(`${LIST_PREFIX}${state.slug}`, JSON.stringify(state));
    } catch (error) {
      if (error instanceof DOMException && error.name === "QuotaExceededError") {
        handleStorageQuotaExceeded(state);
      }
    }
  }

  function handleStorageQuotaExceeded(state: List) {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(LIST_PREFIX))
      .sort((a, b) => {
        const timeA = getStorageItemTimestamp(a);
        const timeB = getStorageItemTimestamp(b);
        return timeA - timeB;
      })
      .some((key) => attemptToSaveAfterRemoval(key, state));
  }

  function getStorageItemTimestamp(key: string): number {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item).updatedAt || 0 : 0;
  }

  function attemptToSaveAfterRemoval(key: string, state: List): boolean {
    localStorage.removeItem(key);
    try {
      localStorage.setItem(`${LIST_PREFIX}${state.slug}`, JSON.stringify(state));
      return true;
    } catch {
      return false;
    }
  }

  function getNewTimestamp(): string {
    return new Date().toISOString();
  }

  function createSlug(name: string): string {
    const baseSlug = name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]+/g, "");
    const hash = Math.random().toString(36).substring(2, 8);
    return `${baseSlug}-${hash}`;
  }

  function getListFromLocalStorage(slug: string): List | null {
    if (!browser) return null;

    try {
      const savedList = localStorage.getItem(`${LIST_PREFIX}${slug}`);
      return savedList ? JSON.parse(savedList) : null;
    } catch (error) {
      console.error("Error parsing saved list from localStorage:", error);
      return null;
    }
  }

  return {
    subscribe,

    createList: (name: string, results: SearchResult[] = []) => {
      const slug = createSlug(name);
      const timestamp = getNewTimestamp();
      const userID = authStore.getUser()?.id;

      const newList: List = {
        id: slug,
        slug,
        name,
        results,
        error: null,
        status: "visible",
        createdAt: timestamp,
        updatedAt: timestamp,
        userID,
        isPublic: false,
      };

      update((lists) => ({
        ...lists,
        [slug]: newList,
      }));

      saveListToLocalStorage(newList);
      listHistoryStore.addList({
        slug,
        name,
        createdAt: new Date(timestamp),
        updatedAt: new Date(timestamp),
        userID,
        isPublic: false,
        totalResults: results.length,
        status: "visible",
      });

      if (browser) {
        goto(`/lists/${slug}`);
      }

      return slug;
    },

    updateListDetails: (slug: string, updates: Partial<List>) => {
      update((lists) => {
        const currentList = lists[slug];
        if (!currentList) return lists;

        const updatedList = {
          ...currentList,
          ...updates,
          updatedAt: getNewTimestamp(),
        };

        saveListToLocalStorage(updatedList);
        listHistoryStore.updateList(slug, {
          name: updates.name,
          updatedAt: new Date(),
          isPublic: updates.isPublic,
          status: updates.status,
        });

        return {
          ...lists,
          [slug]: updatedList,
        };
      });
    },

    addResult: (slug: string, result: SearchResult) => {
      update((lists) => {
        const currentList = lists[slug];
        if (!currentList) return lists;

        const exists = currentList.results.some((r) => r.id === result.id);
        if (exists) return lists;

        const updatedList = {
          ...currentList,
          results: [...currentList.results, result],
          updatedAt: getNewTimestamp(),
        };

        saveListToLocalStorage(updatedList);
        listHistoryStore.updateList(slug, {
          totalResults: updatedList.results.length,
          updatedAt: new Date(),
        });

        return {
          ...lists,
          [slug]: updatedList,
        };
      });
    },

    removeResult: (slug: string, resultId: string) => {
      update((lists) => {
        const currentList = lists[slug];
        if (!currentList) return lists;

        const updatedList = {
          ...currentList,
          results: currentList.results.filter((r) => r.id !== resultId),
          updatedAt: getNewTimestamp(),
        };

        saveListToLocalStorage(updatedList);
        listHistoryStore.updateList(slug, {
          totalResults: updatedList.results.length,
          updatedAt: new Date(),
        });

        return {
          ...lists,
          [slug]: updatedList,
        };
      });
    },

    loadListBySlug: (slug: string) => {
      const savedList = getListFromLocalStorage(slug);
      if (savedList) {
        update((lists) => ({
          ...lists,
          [slug]: {
            ...savedList,
            error: null,
          },
        }));
        return true;
      }
      return false;
    },

    deleteList: (slug: string) => {
      if (browser) {
        localStorage.removeItem(`${LIST_PREFIX}${slug}`);
      }

      update((lists) => {
        const { [slug]: _, ...rest } = lists;
        return rest;
      });

      listHistoryStore.removeList(slug);
    },

    getList: (slug: string) => get({ subscribe })[slug],

    getResults: (slug: string): SearchResult[] => {
      return get({ subscribe })[slug]?.results || [];
    },
  };
}

export const listStore = createListStore();
